import { useState, useRef, useEffect, useCallback } from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { useAlertContext } from '../contexts/AlertContext';
import { useI18n } from '../i18n';

export default function AlertSoundSettings() {
  const { t } = useI18n();
  const { playerSettings, setPlayerSettings } = useAlertContext();
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open]);

  const handleVolumeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPlayerSettings({ ...playerSettings, volume: Number(e.target.value) });
  }, [playerSettings, setPlayerSettings]);

  const handleMuteToggle = useCallback((checked: boolean | 'indeterminate') => {
    setPlayerSettings({ ...playerSettings, muted: checked === true });
  }, [playerSettings, setPlayerSettings]);

  return (
    <div className="relative" ref={panelRef}>
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
        onClick={() => setOpen(!open)}
        aria-label={t('alerts.sound.settings')}
        aria-expanded={open}
        title={t('alerts.sound.settings')}
        data-testid="alert-sound-toggle"
      >
        {playerSettings.muted || playerSettings.volume === 0 ? <VolumeX size={14} /> : <Volume2 size={14} />}
      </Button>

      {open && (
        <div
          className="absolute bottom-[calc(100%+4px)] right-0 w-56 rounded-lg border border-border bg-card p-3 shadow-xl z-[var(--z-dialog)] space-y-3"
          data-testid="alert-sound-popover"
        >
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-xs">{t('alerts.sound.volume')}</Label>
              <span className="text-xs text-muted-foreground text-right min-w-[2.5rem]">{playerSettings.volume}%</span>
            </div>
            <input
              type="range"
              min={0}
              max={100}
              value={playerSettings.volume}
              onChange={handleVolumeChange}
              className="w-full accent-primary h-1.5 rounded-full"
              aria-label={t('alerts.sound.volume')}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={playerSettings.volume}
              data-testid="alert-volume-control"
            />
          </div>
          <div className="flex items-center gap-2 mt-2">
            <Checkbox
              id="alert-mute"
              checked={playerSettings.muted}
              onCheckedChange={handleMuteToggle}
              data-testid="alert-mute-checkbox"
            />
            <Label htmlFor="alert-mute" className="text-sm text-foreground">
              {t('alerts.sound.mute')}
            </Label>
          </div>
        </div>
      )}
    </div>
  );
}
